import { useState, useEffect, useMemo, useRef, useCallback } from 'react'
import Formsy from 'formsy-react'

import { Button, IconButton, Table, TableContainer, TableHead, TableBody, Paper, DialogTitle, DialogContent, DialogActions, 
         Checkbox, FormControl, FormControlLabel } from '@mui/material'
import { Add, Delete, Edit, EditOff, DeleteForever, HelpOutline, } from '@mui/icons-material'
import * as MuiIcons from '@mui/icons-material'

import { D, Loading, SearchField, TRow, TCell, TextFieldFormsy, CustomSelect, ConfirmDelete } from '../include'


// Funções puras utilitárias (fora do componente para não recriar a cada render)
// para tratamento dos nomes dos icones da MUI
const snakeToPascal = str => {
  if (!str) return '';
  return str
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join('');
};

const pascalToSnake = (str) => {
  if (!str) return '';
  return str
    .replace(/([a-z0-9])([A-Z])/g, '$1_$2') // coloca _ antes das maiúsculas
    .toLowerCase();
};


// ==========================================
// COMPONENTE: para manejar a tabela de fatos
// ==========================================
const EditFact = ({ row, edit, api, alert, dialogClose, onSuccess, user }) => {
  const isAdmin = user?.role === 'admin';

  const [formData, setFormData] = useState({
    tipo_de_dado: row?.tipo_de_dado || 'string',
    qualificator: row?.qualificator || '',
    gestao_de_alerta: row?.gerador_de_alerta || false,
    pendencia: row?.conta_pendencia || false,
    sysicon: row?.sys_icon || false,
    iconame: row ? snakeToPascal(row.icon_name) : 'PersonAddAlt1',
    sort: row?.sort ?? 99,
  });

  const [iconPreview, setIconPreview] = useState(row ? snakeToPascal(row.icon_name) : 'PersonAddAlt1');

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleIconBlur = () => {
    // Converte para PascalCase quando o usuário sair do campo
    const pascalName = snakeToPascal(formData.iconame);
    handleChange('iconame', pascalName);
    
    // Atualiza o preview se o ícone existir no MUI
    if (MuiIcons[pascalName]) {
      setIconPreview(pascalName);
    } else {
      setIconPreview(null);
    }
  };

  const handleSave = () => {
    const payload = {
      tipo_de_dado: formData.tipo_de_dado,
      qualificator: formData.qualificator,
      gestao_de_alerta: formData.gestao_de_alerta,
      pendencia: formData.pendencia,
      sysicon: formData.sysicon,
      iconame: pascalToSnake(formData.iconame), // salva em snake_case no BD
      sort: formData.sort,
    };

    const request = !edit
      ? api.post('/Facts/', payload)
      : api.put(`/Fact/${row.id}`, payload);

    request
      .then(r => {
        onSuccess();
        alert(r.msg || "Operação realizada com sucesso!");
      })
      .catch(err => alert(err.message || 'Erro inesperado'));
  };

  const IconComponent = MuiIcons[iconPreview] || HelpOutline;

  return (
    <Formsy onValidSubmit={handleSave}>
      <DialogTitle style={{ minWidth: '400px' }}>
        {edit ? 'Editar Fato' : 'Adicionar Fato'}
      </DialogTitle>

      <DialogContent>
        <D h="0rem" />

        <TextFieldFormsy
          label="Nome do Fato"
          name="Qualificador"
          variant="outlined"
          value={formData.qualificator}
          onChange={v => handleChange('qualificator', v)}
          required
        />

        <D h="0.5rem" />

        <CustomSelect
          label="Tipo"
          value={formData.tipo_de_dado || 'string'}
          options={['bool', 'date', 'float', 'int', 'string']}
          onChange={v => handleChange('tipo_de_dado', v)}
          required
        />

        {isAdmin && (
          <>
            <D h="1rem" />
            <div style={{ fontSize: '1rem', fontWeight: 'bold', color: '#333', borderBottom: '1px solid #ccc', paddingBottom: '0.5rem', marginBottom: '0.5rem' }}>
              Configurações Avançadas
            </div>
            
            <FormControl>
              <FormControlLabel
                labelPlacement="start"
                label="Gerador de Pendência?"
                control={<Checkbox checked={formData.pendencia} onChange={e => handleChange('pendencia', e.target.checked)} />}
              />
            </FormControl>

            <D h="0.2rem" />

            <FormControl>
              <FormControlLabel
                labelPlacement="start"
                label="Fato do Sistema?"
                control={<Checkbox checked={formData.sysicon} onChange={e => handleChange('sysicon', e.target.checked)} />}
              />
            </FormControl>

            <D h="0.5rem" />

            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
              <TextFieldFormsy
                label="Nome do Ícone MUI"
                name="iconame"
                variant="outlined"
                value={formData.iconame}
                onChange={v => handleChange('iconame', v)}
                onBlur={handleIconBlur}
              />
              <IconComponent style={{ fontSize: 36, color: '#555' }} />
            </div>

            <D h="0.5rem" />

            <TextFieldFormsy
              label="Ordem de Sort nos cards"
              name="sort"
              variant="outlined"
              value={formData.sort}
              onChange={v => handleChange('sort', v)}
            />
          </>
        )}

        <D h="1rem" />
      </DialogContent>

      <DialogActions>
        <Button variant="outlined" color="primary" onClick={dialogClose}>
          Cancelar
        </Button>
        <Button variant="outlined" color="primary" type="submit">
          Salvar
        </Button>
      </DialogActions>
    </Formsy>
  );
};


// ==========================================
// COMPONENTE PRINCIPAL: Fact
// ==========================================
export const Fact = ({ api, user, permission, dialog, dialogClose, alert }) => {
  const [factsData, setFactsData] = useState(null); // null aciona o Loading inicial
  const [searchField, setSearchField] = useState('');

  // 1. Criar a referência inicial
  const isFirstRender = useRef(true);
  const isAdminUser = user?.role === 'admin';

  const fetchFacts = useCallback(() => {
    setFactsData(null); // Feedback visual de recarregamento
    api.get('/Facts', { params: { log: true } })
      .then(data => setFactsData(data))
      .catch(err => {
        console.error("Erro ao buscar Fatos:", err);
        setFactsData([]);
      });
  }, [api]);

  useEffect(() => {
    if (isFirstRender.current) {
      fetchFacts();
      isFirstRender.current = false;
    }
  }, [fetchFacts]);

  // Motor de filtragem de busca isolado em memoização
  const filteredFacts = useMemo(() => {
    if (!factsData) return [];
    if (!searchField) return factsData;

    const lowerSearch = searchField.toLowerCase();
    return factsData.filter(row => 
      row.qualificator?.toLowerCase().includes(lowerSearch)
    );
  }, [factsData, searchField]);

  return (
    <D className="col e aF scD" style={{ padding: '2rem' }}>
      {permission === 'w' && (
        <>
          <D className="row w aF">
            <SearchField
              value={searchField}
              onChange={v => setSearchField(v)}
            />
            <D e />
            <D size="2rem" />
            <Button
              variant="contained"
              color="primary"
              onClick={() => dialog(
                <EditFact
                  api={api} alert={alert} user={user}
                  dialogClose={dialogClose} 
                  onSuccess={() => { fetchFacts(); dialogClose(); }} 
                />
              )}
            >
              <Add />Fato
            </Button>
          </D>
          <D h="1rem" />
        </>
      )}

      <Paper sx={{ width: '100%', overflow: 'hidden' }}>
        <TableContainer style={{ height: '100%' }}>
          <Table stickyHeader sx={{ tableLayout: 'fixed' }}>
            <TableHead>
              <TRow sx={{ height: '3rem', background: '#ccc' }}>
                {permission === 'w' && (
                  <>
                    <TCell align="center" sx={{ width: '8ch', maxWidth: '8ch' }}>Editar</TCell>
                    <TCell align="center" sx={{ width: '8ch', maxWidth: '8ch' }}>Excluir</TCell>
                  </>
                )}
                
                <TCell sx={{ width: '8ch', maxWidth: '8ch' }}>Icone</TCell>
                
                {isAdminUser && (
                  <TCell align="center" sx={{ width: '8ch', maxWidth: '8ch' }}>ID</TCell>
                )}
                
                <TCell sx={{ width: '35ch', maxWidth: '40ch', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  Nome do Fato ({filteredFacts.length} fatos)
                </TCell>
                
                <TCell align="center" sx={{ width: '10ch', maxWidth: '15ch', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  Tipo
                </TCell>
                
                <TCell align="center" sx={{ width: '20ch', maxWidth: '20ch', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  Gerador de Alerta
                </TCell>
                
                <TCell align="center" sx={{ width: '18ch', maxWidth: '18ch', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  Fato do Sistema
                </TCell>

                <TCell sx={{ p: 0, m: 0 }} /> {/* Célula Buffer */}
              </TRow>
            </TableHead>

            <TableBody>
              {factsData === null ? (
                <TRow>
                  <TCell component="th" align="center" colSpan={9}><Loading /></TCell>
                </TRow>
              ) : filteredFacts.length > 0 ? (
                filteredFacts.map(row => {
                  const isSysFact = row.sys_icon === true;
                  const isDisabled = !isAdminUser && isSysFact;
                  const pascalIconName = snakeToPascal(row.icon_name);
                  const IconComponent = MuiIcons[pascalIconName] || HelpOutline;

                  return (
                    <TRow key={row.id} row={row} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                      {permission === 'w' && (
                        <>
                          <TCell align="center" component="th">
                            <IconButton
                              style={{ color: isDisabled ? '#ccc' : '#aaa', cursor: isDisabled ? 'not-allowed' : 'pointer' }}
                              disabled={isDisabled}
                              onClick={() => !isDisabled && dialog(
                                <EditFact
                                  edit row={row} api={api} alert={alert} user={user}
                                  dialogClose={dialogClose} 
                                  onSuccess={() => { fetchFacts(); dialogClose(); }} 
                                />
                              )}
                            >
                              {isDisabled ? <EditOff /> : <Edit />}
                            </IconButton>
                          </TCell>
                          <TCell align="center" component="th">
                            <IconButton
                              style={{ color: isDisabled ? '#ccc' : '#aaa', cursor: isDisabled ? 'not-allowed' : 'pointer' }}
                              disabled={isDisabled}
                              onClick={() => !isDisabled && dialog(
                                <ConfirmDelete
                                  title="Excluir Fato"
                                  msg="Tem certeza que deseja excluir o Fato:"
                                  itemName={row.qualificator}
                                  onCancel={dialogClose}
                                  onConfirm={() => {
                                    api.delete('/Fact/' + row.id)
                                      .then(r => {
                                        alert(r.msg);
                                        fetchFacts();
                                        dialogClose();
                                      })
                                      .catch(e => alert(e.msg || 'Erro ao excluir.'));
                                  }}
                                />
                              )}
                            >
                              {isDisabled ? <DeleteForever /> : <Delete />}
                            </IconButton>
                          </TCell>
                        </>
                      )}

                      <TCell align="center">
                        <IconComponent style={{ padding: 0, fontSize: 26, color: '#555' }} />
                      </TCell>

                      {isAdminUser && (
                        <TCell align="center" sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {row.id}
                        </TCell>
                      )}

                      <TCell sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {row.qualificator}
                      </TCell>

                      <TCell align="center" sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {row.tipo_de_dado}
                      </TCell>

                      <TCell align="center">{row.gerador_de_alerta === true ? 'Sim' : 'Não'}</TCell>
                      <TCell align="center">{row.sys_icon === true ? 'Sim' : 'Não'}</TCell>
                      
                      <TCell /> {/* Célula Buffer */}
                    </TRow>
                  );
                })
              ) : (
                <TRow>
                  <TCell component="th" align="center" colSpan={9}>
                    Nenhum registro encontrado
                  </TCell>
                </TRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </D>
  );
};